import { DataObject, DataObjectFactory } from "@/v2/models/base-data-object.js";
import { ProductAttributeFactory, ProductAttributeMapperParameter } from "@/v2/models/product-attributes";
import { ProductGroupFactory, ProductGroupMapperParameter } from "@/v2/models/product-groups";
import { ProductCategoryFactory, ProductCategoryMapperParameter } from "@/v2/models/product-categories";
import { CompanyFactory, CompanyMapperParameter } from "@/v2/models/companies";
import { ProductMediaFactory, MediaMapperParameter } from "@/v2/models/medias";
import { ShippingClassFactory, ShippingClassMapperParameter } from "@/v2/models/shipping_class";
import { TierFactory, TierMapperParameter } from "@/v2/models/tiers";
import { Parameter } from "@/v2/models/parameters";
import { now } from "@/v2/utils/date.js";
export class Product extends DataObject {
    _type = "product";

    id;
    name;
    type;
    status;
    description;
    internal_description;
    volume;
    dimensional_sum;
    weight;
    height;
    length;
    width;
    video;
    sku;
    shipping_class;
    shipping_class_id;
    uom;
    warehouseInventories;
    lead_time;
    tax_included; // setting to include tax in price
    display_price_include_tax; // setting to include tax when display price
    tax_exempted; // company setting to exempt tax
    original_regular_price;
    regular_price;
    sale_price;
    discount_type;
    discount_formula;
    subtotal; // calc subtotal after tax
    subtotal_tax; // calc subtotal tax
    total; // calc total after tax
    total_tax; // calc total tax
    discount_percentage;
    discount_amount;

    display_regular_price; // base price displayed
    display_regular_tax; // price displayd after discount & tax
    display_unit_price; // price displayd after discount & tax
    display_unit_tax; // price displayd after discount & tax
    display_subtotal; // calc subtotal after tax
    display_subtotal_tax; // calc subtotal tax
    display_total; // calc total after tax
    display_total_free = 0;
    display_total_tax; // calc total tax
    display_total_tax_free = 0;

    cost;
    barcode;
    quantity = 0;
    free_quantity = 0;
    images = {};
    categories = {};
    companies = {};
    tiers = {};
    groups = {};
    attributes = {};
    date_valid_from;
    date_valid_to;
    is_accounts_excluded;
    is_tiers_excluded;
    variation_shelf;
    packsize;
    min_price;
    max_price;
    min_quantity;
    max_quantity;
    stock_quantity_overwrite;
    allow_backorders;
    disallow_children_backorders;
    manage_stock;
    stock_quantity;
    in_stock;
    tax_class;
    parent_id;
    parent;
    split_group;
    competitor_product;
    unit_price;
    variation_height;
    variation_weight;
    base_variation_product_id;
    bo_can_delete;
    show_action_menu;
    per_item_pricing = true;
    variation_visibility;
    catalog_visibility;
    favorited;
    redeem_with_coupon_only;
    product_labels;
    promotion_id;
    is_trade_promotion_managed;
    promotion_code;
    promotion_version;
    promotion_detail_url;

    getWeight() {
        return this.weight;
    }

    getPacksize() {
        return this.packsize;
    }

    getVolume() {
        return this.length * this.width * this.height;
    }

    getDimensionalSum() {
        return this.length + this.width + this.height;
    }

    getPrice() {
        return this.sale_price ?? this.regular_price;
    }

    getOriginalPrice() {
        return this.regular_price;
    }

    getOriginalRegularPrice() {
        return this.original_regular_price;
    }

    getDiscountType() {
        return this.discount_type;
    }

    getDiscountFormula() {
        return this.discount_formula;
    }

    getQuantity() {
        return this.quantity;
    }

    getCurrentPrice() {
        return this.getPrice();
    }

    getTotal(quantity = null) {
        return this.getPrice() * (quantity ?? this.quantity);
    }

    getSubtotal(quantity = null) {
        return this.getOriginalPrice() * (quantity ?? this.quantity);
    }

    getPriceAfterTax(price) {
        if (this.tax_exempted) {
            return price;
        }
        let percentage = this.tax_class?.percentage;
        if (this.tax_included && percentage) {
            return price / (1 + percentage / 100);
        }
        return price;
    }

    getDisplayUnitPrice() {
        return this.getPrice();
    }

    getDisplayPriceAfterTax(price) {
        let percentage = this.tax_class?.percentage;
        if (!percentage || this.tax_exempted || this.tax_included == this.display_price_include_tax) {
            return price;
        } else if (this.tax_included && !this.display_price_include_tax) {
            return price / (1 + percentage / 100);
        } else {
            return price * (1 + percentage / 100);
        }
    }

    updateProductPrice() {
        // calc product price
        this.setProductDisplayRegularPrice();
        this.setProductDisplayUnitPrice();
        this.setProductUnitPrice();

        // calc subtotal & total
        this.setProductDisplaySubtotal();
        this.setProductDisplayTotal();
        this.setProductSubtotal();
        this.setProductTotal();
    }

    setProductDisplayRegularPrice() {
        // this.display_regular_price = this.getDisplayPriceAfterTax(this.original_regular_price);
        // this.display_regular_tax = this.original_regular_price - this.display_regular_price;
        this.display_regular_price = this.getDisplayPriceAfterTax(this.regular_price);
        this.display_regular_tax = this.regular_price - this.display_regular_price;
    }
    setProductDisplayUnitPrice() {
        // let price = this.getOriginalRegularPrice();
        let price = this.getPrice();
        if (this.discount_percentage) {
            price -= (price * this.discount_percentage) / 100;
        }
        if (this.discount_amount) {
            price -= this.discount_amount;
        }
        this.display_unit_price = this.getDisplayPriceAfterTax(price);
        this.display_unit_tax = price - this.display_unit_price;
    }
    setProductDisplaySubtotal() {
        this.display_subtotal = this.display_regular_price * (this.quantity - this.free_quantity);

        this.display_subtotal_tax = this.display_regular_tax * (this.quantity - this.free_quantity);
    }
    setProductDisplayTotal() {
        this.display_total = this.display_unit_price * (this.quantity - this.free_quantity);

        this.display_total_tax = this.display_unit_tax * (this.quantity - this.free_quantity);
    }

    setProductUnitPrice() {
        let price = this.getPrice();
        if (this.discount_percentage) {
            price -= (price * this.discount_percentage) / 100;
        }
        if (this.discount_amount) {
            price -= this.discount_amount;
        }

        this.unit_price = price;
    }
    calculateProductTotal(price) {
        let subtotal = price * this.quantity;
        subtotal = this.getPriceAfterTax(subtotal);
        return subtotal;
    }
    setProductSubtotal() {
        let subtotal = this.regular_price * (this.quantity - this.free_quantity);

        let subtotal_tax = this.getDbTax(subtotal);
        this.subtotal_tax = subtotal_tax;
        if (this.tax_included) {
            this.subtotal = subtotal - this.subtotal_tax;
        } else {
            this.subtotal = subtotal;
        }
    }
    setProductTotal() {
        let total = this.unit_price * (this.quantity - this.free_quantity);

        let total_tax = this.getDbTax(total);
        this.total_tax = total_tax;
        if (this.tax_included) {
            this.total = total - total_tax;
        } else {
            this.total = total;
        }
    }
    getDbTax(price_before_tax) {
        if (this.tax_exempted) {
            return 0;
        }
        let percentage = this.tax_class?.percentage;
        if (percentage) {
            let price_after_tax = price_before_tax;
            if (this.tax_included) {
                price_after_tax = price_before_tax / (1 + percentage / 100);
                return price_before_tax - price_after_tax;
            } else {
                price_after_tax = price_before_tax * (1 + percentage / 100);
                return price_after_tax - price_before_tax;
            }
        }
        return 0;
    }

    getStatus() {
        const currentDate = now();

        if (this.status === "publish") {
            if (this.date_valid_to && currentDate > this.date_valid_to) {
                return "expired";
            }

            if (this.date_valid_from && currentDate >= this.date_valid_from) {
                return "ongoing";
            }
        }

        return this.status;
    }
}

export class BundleProduct extends Product {
    _type = "bundle_product";

    components = {}; //Map
    per_item_pricing;
    ratio_1;
    ratio_2;

    getCurrentPrice() {
        if (!this.per_item_pricing) {
            return this.regular_price;
        }
        return this.getPrice();
    }

    getWeight() {
        return this.getComponentTotalWeight();
    }

    getVolume() {
        return this.getComponentTotalVolume();
    }

    getDimensionalSum() {
        return this.getComponentTotalDimensionalSum();
    }

    getSubtotal(quantity = null) {
        if (!this.per_item_pricing) {
            return this.regular_price * this.quantity;
        }

        return this.getOriginalPrice() * (quantity ?? this.quantity);
    }

    getTotal(quantity = null) {
        if (!this.per_item_pricing) {
            return this.regular_price * this.quantity;
        }

        return this.getComponentTotal() * (quantity ?? this.quantity);
    }

    getPrice() {
        return this.getComponentTotal();
    }

    getOriginalPrice() {
        return this.getComponentSubtotal();
    }

    getComponentTotalWeight() {
        let totalWeight = 0;
        Object.values(this.components).forEach((component) => (totalWeight += component.getWeight()));
        return totalWeight;
    }

    getComponentTotalVolume() {
        let totalVolume = 0;
        Object.values(this.components).forEach((component) => (totalVolume += component.getVolume()));
        return totalVolume;
    }

    getComponentTotalDimensionalSum() {
        let totalDimensionalSum = 0;
        Object.values(this.components).forEach((component) => (totalDimensionalSum += component.getDimensionalSum()));
        return totalDimensionalSum;
    }

    getComponentTotal() {
        let total = 0;
        Object.values(this.components).forEach((component) => (total += component.getTotal())) * this.quantity;
        return total;
    }

    getComponentSubtotal() {
        let subtotal = 0;
        Object.values(this.components).forEach((component) => (subtotal += component.getSubtotal())) * this.quantity;
        return subtotal;
    }

    getTotalForSubmission() {
        let total = 0;

        Object.values(this.components).forEach((component) => {
            total += component.getTotalForSubmission();
        });

        return parseFloat((total * this.quantity).toFixed(2));
    }

    getSubtotalForSubmission() {
        let total = 0;

        Object.values(this.components).forEach((component) => {
            total += component.getSubtotalForSubmission();
        });

        return parseFloat((total * this.quantity).toFixed(2));
    }
    getSubtotalTax() {
        let total = 0;

        Object.values(this.components).forEach((component) => {
            total += component.getSubtotalTax();
        });

        return parseFloat((total * this.quantity).toFixed(2));
    }
    getTotalTax() {
        let total = 0;

        Object.values(this.components).forEach((component) => {
            total += component.getTotalTax();
        });

        return parseFloat((total * this.quantity).toFixed(2));
    }
    getDisplayRegularPrice() {
        let regular_price = 0;

        Object.values(this.components).forEach((component) => {
            regular_price += component.getDisplayRegularPrice();
        }) * this.quantity;

        return regular_price;
    }

    getDisplayUnitPrice() {
        let price = 0;

        Object.values(this.components).forEach((component) => {
            price += component.getDisplayUnitPrice();
        }) * this.quantity;

        return price;
    }

    getDisplayTotal() {
        return this.getDisplayUnitPrice() * this.quantity;
    }

    setProductDefaultQuantity() {
        Object.values(this.components).forEach((component) => {
            let defaultQty = 0;
            if (component.enable_flexi_combo) {
                defaultQty = component.scale_up_ratio || component.min_quantity || 1;
            } else if (component.min_quantity > 0) {
                defaultQty = component.min_quantity;
            }

            if (defaultQty > 0) {
                let priceArray = {};
                let productCount = 0;
                Object.values(component.products).forEach((product) => {
                    Object.values(product.variations).forEach((variation) => {
                        if (this.per_item_pricing) {
                            priceArray[product.id + "-" + variation.id] = variation.getPrice();
                        } else {
                            priceArray[product.id + "-" + variation.id] = 0;
                        }
                        productCount++;
                    });
                });

                if (productCount !== 1) {
                    return;
                }

                // set quantity
                let lowestPrice = Infinity;
                let lowestPriceIndex = null;
                Object.keys(priceArray).forEach((key) => {
                    const currentPrice = priceArray[key];

                    if (currentPrice < lowestPrice) {
                        lowestPrice = currentPrice;
                        lowestPriceIndex = key;
                    }
                });

                if (lowestPriceIndex) {
                    let indexes = lowestPriceIndex.split("-");
                    let productIndex = indexes[0];
                    let variationIndex = indexes[1];
                    if (component.products[productIndex]?.variations[variationIndex]) {
                        component.products[productIndex].variations[variationIndex].quantity = defaultQty;
                        component.products[productIndex].variations[variationIndex].updateProductPrice();
                    }
                }
            }
        });
    }
}

export class VariableProduct extends Product {
    _type = "variable_product";

    variations = {}; //Map
    default_variation;
    base_variation;
    selected_variation_id = null;
    selected_uom = null;
    uom = null;
    bulk_variation_input = null;

    getWeight() {
        if (this.selected_variation_id == null) {
            return null;
        }

        return this.getSelectedVariation().getWeight();
    }

    getPacksize() {
        if (this.selected_variation_id == null) {
            return null;
        }

        return this.getSelectedVariation().getPacksize();
    }

    getVolume() {
        if (this.selected_variation_id == null) {
            return null;
        }

        return this.getSelectedVariation().getVolume();
    }

    getDimensionalSum() {
        if (this.selected_variation_id == null) {
            return null;
        }

        return this.getSelectedVariation().getDimensionalSum();
    }

    getCurrentPrice() {
        if (this.selected_variation_id != null) {
            return this.getSelectedVariation().getCurrentPrice();
        }

        return this.getPrice();
    }

    getOriginalPrice() {
        if (this.selected_variation_id != null) {
            return this.getSelectedVariation().getOriginalPrice();
        }

        return this.regular_price;
    }

    getOriginalRegularPrice() {
        if (this.selected_variation_id != null) {
            return this.getSelectedVariation().getOriginalRegularPrice();
        }

        return this.original_regular_price;
    }

    getDiscountType() {
        if (this.selected_variation_id != null) {
            return this.getSelectedVariation().getDiscountType();
        }

        return this.discount_type;
    }

    getDiscountFormula() {
        if (this.selected_variation_id != null) {
            return this.getSelectedVariation().getDiscountFormula();
        }

        return this.discount_formula;
    }

    getTotal(quantity = null) {
        const variation = this.getSelectedVariation();
        if (variation == null) {
            return 0.0;
        }

        return (quantity ?? this.quantity) * variation.getCurrentPrice();
    }

    getSubtotal(quantity = null) {
        const variation = this.getSelectedVariation();
        if (variation == null) {
            return 0.0;
        }

        return (quantity ?? this.quantity) * variation.getOriginalPrice();
    }

    getSelectedVariation() {
        return this.selected_variation_id ? this.variations[this.selected_variation_id] : null;
    }

    getSelectedUom() {
        return this.selected_uom ? this.uom[this.selected_uom] : null;
    }

    selectUom(uom) {
        this.selected_uom = uom;
        this.selectVariationByUom(uom);
    }

    selectVariation(id) {
        this.selected_variation_id = id;
        this.selected_uom = this.variations[id].uom.slug;
    }

    selectVariationByUom(uom) {
        this.selected_variation_id = this.getVariationByUom(uom);
    }

    getVariationByUom(uom) {
        for (const variation of Object.values(this.variations)) {
            if (variation.uom.slug === uom) {
                return variation.id;
            }
        }

        return null;
    }

    // Get base variation where uom has conversion rate 1
    getBaseVariation() {
        for (const variation of Object.values(this.variations)) {
            const uom = variation.uom;
            if (uom.conversion_rate == 1) {
                return variation;
            }
        }
        return null;
    }

    // Get default uom
    getDefaultVariation() {
        for (const variation of Object.values(this.variations)) {
            const uom = variation.uom;
            if (uom.product_id == this.default_variation?.id) {
                return variation;
            }
        }
        return null;
    }
}

export class VariationProduct extends Product {
    _type = "variation_product";
    boms;
    uom;
    parent_name;
    hide;
    conv_rate_group;
    attributes_index_by_slug = {};
    points_earned;
    points_earned_type;
    product_attributes;
    attribute_options;
}

export class Uom extends DataObject {
    _type = "uom";
    id;
    slug;
    name;
    conversion_rate = 1;
    product_id;
    hide;
    bo_can_delete;
    show_action_menu;
}

// BOM frontend model
export class Bom extends DataObject {
    _type = "bom";
    material_id;
    warehouse_id;
    quantity = 1;
    product;
    bo_can_delete;
    show_action_menu;
}

//BOM mapperParameter
export class BomMapperParameter extends Parameter {
    // The same mapper function we defined in mixins/foo.js will be assigned to this property.
    // The name here MUST match the name of the mapper in the bar.js mixin ('_fooMapper' in this case)
    bomMapper;
}

export class BundleComponent extends DataObject {
    _type = "bundle_component";

    id;
    bundle_id;
    name;
    number_of_different_products_allowed;
    min_quantity = 0;
    max_quantity;
    different_sku_free_quantity;
    discount_on_regular_price;
    discount_percentage;
    discount_amount;
    price;
    composite_minimum_order;
    enable_flexi_combo;
    minimum_different_products_allowed;
    maximum_different_products_allowed;
    scale_up_ratio;
    optional;
    sequence;

    // In reality, components are directly linked to variations - we just return its product variable product in order
    // to access information in the variable product.
    // Hence, it is helpful to treat the products in this map not as variable products, but as an aggregation of variation products with some common properties.
    //
    // IMPORTANT
    // Variable products in this map also do not contain it's full list of variations - only those variations that are assigned to this component.
    products = {};

    getWeight() {
        let totalWeight = 0;

        Object.values(this.products).forEach((product) => {
            Object.values(product.variations).forEach(
                (variation) => (totalWeight += variation.getWeight() * variation.quantity)
            );
        });

        return totalWeight;
    }

    getVolume() {
        let totalVolume = 0;

        Object.values(this.products).forEach((product) => {
            Object.values(product.variations).forEach(
                (variation) => (totalVolume += variation.getVolume() * variation.quantity)
            );
        });

        return totalVolume;
    }

    getDimensionalSum() {
        let totalDimensionalSum = 0;

        Object.values(this.products).forEach((product) => {
            Object.values(product.variations).forEach(
                (variation) => (totalDimensionalSum += variation.getDimensionalSum() * variation.quantity)
            );
        });

        return totalDimensionalSum;
    }

    getDisplayTotal() {
        let total = 0;

        Object.values(this.products).forEach((product) =>
            Object.values(product.variations).forEach((variation) => (total += variation.display_total))
        );

        return total;
    }

    getTotal() {
        let total = 0;

        Object.values(this.products).forEach((product) =>
            Object.values(product.variations).forEach((variation) => (total += variation.total))
        );

        return total;
    }

    calculateTotal(total, quantity) {
        if (this.price != null) {
            return this.price * quantity;
        }

        return this.applyDiscount(total);
    }

    applyDiscount(total) {
        if (this.discount_percentage) {
            total = total * ((100 - this.discount_percentage) / 100);
        } else if (this.discount_amount) {
            total = total - this.discount_amount;
            total = total < 0 ? 0 : total;
        }

        return total;
    }

    getProductTotal(product_id) {
        const product = this.products[product_id];
        if (product == null) {
            return 0.0;
        }

        return this.applyDiscount(product.getTotal());
    }

    getProductSubtotal(product_id) {
        const product = this.products[product_id];
        if (product == null) {
            return 0.0;
        }

        return product.getSubtotal();
    }

    getTotalForSubmission() {
        let total = 0;
        Object.values(this.products).forEach((product) =>
            Object.values(product.variations).forEach((variation) => {
                total += variation.total;
            })
        );

        return total;
    }

    getSubtotalForSubmission() {
        let total = 0;
        Object.values(this.products).forEach((product) =>
            Object.values(product.variations).forEach((variation) => {
                total += variation.subtotal;
            })
        );

        return total;
    }
    getSubtotalTax() {
        let total = 0;
        Object.values(this.products).forEach((product) =>
            Object.values(product.variations).forEach((variation) => (total += variation.subtotal_tax))
        );

        return total;
    }
    getTotalTax() {
        let total = 0;
        Object.values(this.products).forEach((product) =>
            Object.values(product.variations).forEach((variation) => (total += variation.total_tax))
        );

        return total;
    }

    getSubtotal() {
        if (this.price != null) {
            return this.price * this.getQuantity();
        }

        let total = 0;
        Object.values(this.products).forEach((product) =>
            Object.values(product.variations).forEach((variation) => (total += variation.subtotal))
        );

        return total;
    }

    getQuantity() {
        let total = 0;
        Object.values(this.products).forEach((product) =>
            Object.values(product.variations).forEach((variation) => (total += variation.quantity))
        );
        return total;
    }

    getProductQuantity(productId) {
        let total = 0;
        Object.values(this.products[productId].variations).forEach((variation) => (total += variation.quantity));
        return total;
    }

    getDisplayRegularPrice() {
        let total = 0;
        Object.values(this.products).forEach((product) =>
            Object.values(product.variations).forEach((variation) => {
                total += variation.display_regular_price * variation.quantity;
            })
        );

        return total;
    }

    getDisplayUnitPrice() {
        let total = 0;
        Object.values(this.products).forEach((product) =>
            Object.values(product.variations).forEach((variation) => {
                total += variation.display_total;
            })
        );

        return total;
    }
}

// product category have its own model ady

// export class ProductCategory extends DataObject {
//     _type = "product_category";

//     id;
//     name;
// }

export class ProductMapperParameter extends Parameter {
    productMapper;
    uomMapper;
    componentMapperParameter = new ComponentMapperParameter();
    productGroupMapperParameter = new ProductGroupMapperParameter();
    productCategoryMapperParameter = new ProductCategoryMapperParameter();
    shippingClassMapperParameter = new ShippingClassMapperParameter();
    companyMapperParameter = new CompanyMapperParameter();
    tierMapperParameter = new TierMapperParameter();
    mediaMapperParameter = new MediaMapperParameter();
    productAttributeMapperParameter = new ProductAttributeMapperParameter();
    bomMapperParameter = new BomMapperParameter();
}

export class ComponentMapperParameter extends Parameter {
    productMapper;
    componentMapper;
    uomMapper;
}

export class ProductFactory {
    static bindings = {
        simple: Product,
        bundle: BundleProduct,
        variable: VariableProduct,
        variation: VariationProduct,
    };

    /**
     * Creates a new instance of a DataObject.
     * @function
     * @static
     * @param {Object, null} data Data to construct the Product object with.
     * Also creates a BundleComponent object using BundleComponentFactory if data is not null.
     * @returns {Product}
     */
    static construct(productMapperParameter, data = null, type = null) {
        const productType = type ?? data.type;
        if (data != null) {
            const constructor = this.bindings[productType];
            let product = DataObjectFactory.getInstance().construct(
                constructor,
                data ? productMapperParameter.productMapper(data) : null
            );

            // extract the "custom-{field}" elements from the data object
            const customFields = Object.keys(data)
                .filter((key) => key.startsWith("custom-"))
                .reduce((obj, key) => {
                    obj[key] = data[key];
                    return obj;
                }, {});

            // adding custom fields to the company object
            product = Object.assign(product, customFields);

            if (productType === "bundle" && data.components != null) {
                let map = {};
                data.components.forEach((component) => {
                    const bundleComponent = BundleComponentFactory.construct(
                        productMapperParameter.componentMapperParameter,
                        productMapperParameter,
                        component
                    );
                    map[component.id] = bundleComponent;
                });
                product.components = map;

                // TODO
                // Product categories are needed in products, need to construct an object for it
                // Refactor: Data transfer object used as common parameters that can be reuse among different product types
            }

            if (productType === "variable" && data.variations != null) {
                if (data.variations != null) {
                    let map = {};
                    data.variations.forEach((variationData) => {
                        const variation = this.construct(productMapperParameter, variationData);
                        map[variation.id] = variation;
                    });
                    product.variations = map;

                    // TODO
                    // Product categories are needed in products, need to construct an object for it
                    // Refactor: Data transfer object used as common parameters that can be reuse among different product types
                }

                if (data.uom != null) {
                    let map = {};
                    data.uom.forEach((uomData) => {
                        const uom = DataObjectFactory.getInstance().construct(
                            Uom,
                            uomData ? productMapperParameter.uomMapper(uomData) : null
                        );
                        map[uom.slug] = uom;
                    });
                    product.uom = map;
                }

                if (data.bulk_variation_input != null) {
                    product.bulk_variation_input = data.bulk_variation_input;
                }
            }

            if (data.groups != null) {
                let groups = {};
                data.groups.forEach((productGroupData) => {
                    const productGroup = ProductGroupFactory.construct(
                        productMapperParameter.productGroupMapperParameter,
                        productGroupData
                    );
                    groups[productGroup.id] = productGroup;
                });
                product.groups = groups;
            }

            if (data.categories != null) {
                let temp = {};
                data.categories.forEach((data) => {
                    const model = ProductCategoryFactory.construct(
                        productMapperParameter.productCategoryMapperParameter,
                        data
                    );
                    temp[model.id] = model;
                });
                product.categories = temp;
            }

            if (data.companies != null) {
                let temp = {};
                data.companies.forEach((data) => {
                    const model = CompanyFactory.construct(productMapperParameter.companyMapperParameter, data);
                    temp[model.id] = model;
                });
                product.companies = temp;
            }

            //attributes
            if (data.attributes != null) {
                let temp = {};
                data.attributes.forEach((data) => {
                    const model = ProductAttributeFactory.construct(
                        productMapperParameter.productAttributeMapperParameter,
                        data
                    );
                    temp[model.id] = model;
                });
                product.attributes = temp;
            }

            if (data.tiers != null) {
                let temp = {};
                data.tiers.forEach((data) => {
                    const model = TierFactory.construct(productMapperParameter.tierMapperParameter, data);
                    temp[model.id] = model;
                });
                product.tiers = temp;
            }

            if (data.images != null) {
                let temp = {};
                data.images.forEach((data) => {
                    const model = ProductMediaFactory.construct(productMapperParameter.mediaMapperParameter, data);
                    temp[model.id] = model;
                });
                product.images = temp;
            }

            // create shipping class model object inside product model
            if (data.shipping_class != null) {
                const model = ShippingClassFactory.construct(
                    productMapperParameter.shippingClassMapperParameter,
                    data.shipping_class
                );

                product.shipping_class = model;
            }

            if (productType === "variation") {
                // data.uom comes from API payload (Transformer - DB)
                if (data.uom != null) {
                    product.uom = DataObjectFactory.getInstance().construct(
                        Uom,
                        data.uom ? productMapperParameter.uomMapper(data.uom) : null
                    );
                }

                // Boms
                let temp = {};
                data.boms?.forEach((bom) => {
                    const bomModel = BomFactory.construct(
                        productMapperParameter.bomMapperParameter,
                        productMapperParameter,
                        bom ?? null
                    );
                    if (bomModel) {
                        // console.log('bomModel', bomModel);
                        temp[bomModel.material_id] = bomModel;
                    }
                });
                product.boms = temp;

                if (data.attributes != null) {
                    Object.values(data.attributes).forEach((attribute) => {
                        if (attribute.options.length > 0) {
                            product.attributes_index_by_slug[attribute.name] = Object.values(attribute.options)[0].name;
                        }
                    });
                }

                if (data.conv_rate_group != null) {
                    product.conv_rate_group = data.conv_rate_group;
                }
            }

            return product;
        }

        return null;
    }
}

export class BundleComponentFactory {
    /**
     * Creates a new instance of a DataObject.
     * @function
     * @static
     * @param {Object, null} data Data to construct the BundleComponent object with.
     * @returns {BundleComponent}
     */
    static construct(componentMapperParameter, productMapperParameter, data = null) {
        if (data != null) {
            let component = DataObjectFactory.getInstance().construct(
                BundleComponent,
                data ? componentMapperParameter.componentMapper(data) : null
            );

            if (data.products != null) {
                let temp = {};
                data.products.forEach((data) => {
                    const model = ProductFactory.construct(productMapperParameter, data);
                    temp[model.id] = model;
                });
                component.products = temp;
            }

            return component;
        }

        return null;
    }
}

// Factories should implement a 'construct' method that accepts a mapper parameter and data as arguments.
export class BomFactory {
    static construct(bomMapperParameter, productMapperParameter, data = null) {
        // console.log('BomFactory->data', data);
        if (data != null) {
            // Initialize the model (class Bom) with the data (db) mapped by the mapper function
            let bomModel = DataObjectFactory.getInstance().constructWithMapper(Bom, bomMapperParameter.bomMapper, data);

            // Here we construct the Product object by calling ProductFactory with the data and ProductMapperParameter, which may in turn call other factories to construct child models.
            if (data.product != null) {
                bomModel.product = ProductFactory.construct(productMapperParameter, data.product);
            }
            return bomModel;
        }
        return null;
    }
}
